import type { LifeDimensions, LifeStage, LifeState } from '../domain/life-state'

const dimensionLabels: Record<keyof LifeDimensions, string> = {
  energy: '能量',
  curiosity: '好奇',
  connection: '连接',
  memory: '记忆',
  balance: '平衡',
}

const stageLabels: Record<LifeStage, string> = {
  embryonic: '初生期',
  awakening: '觉醒期',
  perception: '感知期',
  resonance: '共振期',
}

export class StatusPanel {
  private readonly closeButton: HTMLButtonElement | null

  private readonly handleClose = (): void => {
    this.toggle(false)
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.region.hidden) {
      return
    }

    event.preventDefault()
    this.toggle(false)
  }

  constructor(
    private readonly region: HTMLElement,
    private readonly dimensionsList: HTMLElement,
    private readonly toggleButton: HTMLButtonElement,
    private readonly summary: HTMLElement | null = document.querySelector('#life-summary'),
    private readonly updated: HTMLElement | null = document.querySelector('#life-updated'),
  ) {
    this.closeButton = this.region.querySelector<HTMLButtonElement>('#status-close')
    this.closeButton?.addEventListener('click', this.handleClose)
    document.addEventListener('keydown', this.handleKeydown)
  }

  render(state: LifeState): void {
    this.setWaiting(false)
    this.dimensionsList.replaceChildren(
      ...Object.entries(dimensionLabels).map(([key, label]) => {
        const value = state.dimensions[key as keyof LifeDimensions]
        const roundedValue = Math.round(value)
        const row = document.createElement('div')
        const labelElement = document.createElement('span')
        const output = document.createElement('output')
        const meter = document.createElement('meter')

        row.className = 'observation-row'
        labelElement.textContent = label
        output.textContent = String(roundedValue)
        meter.min = 0
        meter.max = 100
        meter.value = value
        meter.setAttribute('aria-label', `${label} ${roundedValue}`)
        row.append(labelElement, output, meter)
        return row
      }),
    )

    if (this.summary) {
      const stage = document.createElement('strong')
      const age = document.createElement('span')
      stage.textContent = stageLabels[state.stage]
      age.textContent = `第 ${state.ageDays} 天`
      this.summary.replaceChildren(stage, age)
    }

    if (this.updated) {
      const date = new Date(state.updatedAt)
      this.updated.textContent = `生命信号更新于 ${new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)}`
    }
  }

  setWaiting(waiting: boolean): void {
    if (waiting) {
      this.region.dataset.signal = 'waiting'
      this.summary?.replaceChildren('等待生命信号')
      return
    }

    const wasWaiting = this.region.dataset.signal === 'waiting'
    delete this.region.dataset.signal
    if (wasWaiting) {
      this.summary?.replaceChildren()
    }
  }

  toggle(force?: boolean): void {
    const wasOpen = !this.region.hidden
    const open = force ?? !wasOpen
    this.region.hidden = !open
    this.toggleButton.setAttribute('aria-expanded', String(open))

    if (open) {
      this.region.setAttribute('data-open', '')
      this.region.focus({ preventScroll: true })
      return
    }

    this.region.removeAttribute('data-open')
    if (wasOpen) {
      this.toggleButton.focus({ preventScroll: true })
    }
  }

  dispose(): void {
    this.closeButton?.removeEventListener('click', this.handleClose)
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
